import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import get from 'lodash/get';

import { getWeek, getWeekStart, getWeekEnd } from '../RepVisitsSearchPage/date-utils';
import { AVAILABILITY_FAIRS, AVAILABILITY_VISITS } from '../HighSchoolAvailability/constants';
import { REP_VISITS_SUBSCRIPTION } from '../Authorization/constants';

import VisitsAvailabilitySingle from '../VisitsAvailabilitySingle';
import VisitRequestContainer from '../VisitRequest';
import FairsAvailabilityContainer from '../FairsAvailability';
import RepVisitsWeekOf from '../RepVisitsSearchPage/repvisits-week-of';
import TravelPlanFooter from './travel-plan-footer';

import Button from '../../components/Button';
import DatePicker from '../../components/DatePicker';
import Grid from '../../components/Grid';
import Icon from '../../components/Icon';
import Popup from '../../components/Popup';
import Toast from '../../components/Toast';
import './styles.css';
import { onSubscriptionActive } from '../../utils/subscriptions';

const messages = defineMessages({
  title: {
    id: 'community.availabilitySidebar.title',
    defaultMessage: 'Repvisits Availability',
  },
  prevWeek: {
    id: 'repvisits.search.prevWeek',
    defaultMessage: 'Previous week',
  },
  nextWeek: {
    id: 'repvisits.search.nextWeek',
    defaultMessage: 'Next week',
  },
  showingWeek: {
    id: 'community.availabilitySidebar.showingWeek',
    defaultMessage: 'Showing week: ',
  },
  selectWeek: {
    id: 'community.availabilitySidebar.goToDate',
    defaultMessage: 'Go to date',
  },
  successDescription: {
    id: 'repvisits.travel.plan.successDescription',
    defaultMessage: 'School added to Travel Plan',
  },
  failureDescription: {
    id: 'repvisits.travel.plan.failureDescription',
    defaultMessage: 'Something unexpected happened. Please try your request again.',
  },
  noRepVisits: {
    id: 'repvisits.school.hasNonePublished',
    defaultMessage: 'This high school has not published a schedule for visits.',
  },
});

export const AddToTravelPlanFooter = onSubscriptionActive(REP_VISITS_SUBSCRIPTION)(TravelPlanFooter);

class Availability extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    closeFunction: PropTypes.func.isRequired,
    highSchoolBySchoolId: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      hasCollegeFairs: PropTypes.bool,
      hasRepVisits: PropTypes.bool,
    }),
    schoolId: PropTypes.string.isRequired,
    hideVisits: PropTypes.bool,
    hideFairs: PropTypes.bool,
    disableScheduleModal: PropTypes.bool, // override the display of the schedule modal
    onAppointmentClick: PropTypes.func, // override the behavior of clicking on a pill
    addTravelPlan: PropTypes.func.isRequired,
  }

  successNotification = <Toast
    active
    title=""
    kind="success"
    description={this.props.intl.formatMessage(messages.successDescription)}
    onDeactivate={() => this.setState({ notification: null })}
  />;

  failureNotification = <Toast
    active
    title=""
    kind="error"
    description={this.props.intl.formatMessage(messages.failureDescription)}
    onDeactivate={() => this.setState({ notification: null })}
  />;

  constructor(props) {
    super(props);

    const { hideVisits, highSchoolBySchoolId } = props;
    const date = new Date();
    date.setDate(date.getDate() + 7);

    this.state = {
      dateFrom: getWeekStart(date),
      dateTo: getWeekEnd(date),
      showDatePicker: false,
      availability: hideVisits ? AVAILABILITY_FAIRS : AVAILABILITY_VISITS,
      scheduleModalOpen: false,
      dayId: null,
      appointmentId: null,
      notification: null,
      webInstructions: null,
    };

    if (highSchoolBySchoolId && !highSchoolBySchoolId.hasRepVisits && highSchoolBySchoolId.hasCollegeFairs) {
      this.state.availability = AVAILABILITY_FAIRS; // change the initial tab to be "fairs"
    }
  }

  setFairs = () => {
    this.setState({ availability: AVAILABILITY_FAIRS });
  }

  setVisits = () => {
    this.setState({ availability: AVAILABILITY_VISITS });
  }

  handleMutationFailure = (transaction) => {
    this.handleTravelPlanCallback(false);
  }

  handleMutationSuccess = (transaction) => {
    this.handleTravelPlanCallback(true);
  }

  handleTravelPlanCallback = (status) => {
    if (!status) {
      this.setState({ notification: this.failureNotification });
    } else {
      this.setState({ notification: this.successNotification });
    }
  };

  addTravelPlan = (schoolId) => () => {
    const input = { highSchoolId: schoolId };
    this.props.addTravelPlan({ variables: { input } })
      .then(this.handleMutationSuccess)
      .catch(this.handleMutationFailure);
  };

  openScheduleModal = (schoolId, dayId, appointmentId, webInstructions) => {
    if (typeof this.props.onAppointmentClick === 'function') {
      this.props.onAppointmentClick(schoolId, dayId, appointmentId);
    } else {
      this.setState({ dayId, appointmentId, webInstructions, scheduleModalOpen: true });
    }
  };

  closeScheduleModal = () => {
    this.setState({ scheduleModalOpen: false });
  }

  handleDatePickerChanged = (day) => {
    this.setState({
      dateFrom: getWeekStart(day),
      dateTo: getWeekEnd(day),
      showDatePicker: false,
    });
  };

  changeDates(days) {
    this.setState((prevState) => {
      const newFrom = new Date(prevState.dateFrom);
      newFrom.setDate(newFrom.getDate() + days);
      const newTo = new Date(prevState.dateTo);
      newTo.setDate(newTo.getDate() + days);
      return { dateFrom: newFrom, dateTo: newTo };
    });
  }

  toggleDatePicker = () => {
    this.setState((prevState) => ({ showDatePicker: !prevState.showDatePicker }));
  };

  viewPrevWeek = (event) => {
    this.changeDates(-7);
  };

  viewNextWeek = (event) => {
    this.changeDates(7);
  };

  render() {
    const { highSchoolBySchoolId, closeFunction, intl, schoolId, hideFairs, hideVisits, disableScheduleModal } = this.props;
    const { dateFrom, dateTo, showDatePicker, availability, dayId, appointmentId, scheduleModalOpen, notification } = this.state;
    const schoolName = get(highSchoolBySchoolId, 'name');
    const inTravelPlan = get(highSchoolBySchoolId, 'isOnViewersTravelPlan');
    const hasRepVisits = get(highSchoolBySchoolId, 'hasRepVisits', false);
    const fairsSelected = (availability === AVAILABILITY_FAIRS);
    const fairsStyle = (fairsSelected) ? 'visits-fair-selected' : 'visits-fair';
    const visitsStyle = (!fairsSelected) ? 'visits-fair-selected' : 'visits-fair';

    // allow props to override defaults
    const showScheduleModal = disableScheduleModal ?
      false :
      scheduleModalOpen;

    const week = getWeek(dateFrom);
    const params = {
      id: schoolId,
      fromDate: week[0],
      toDate: week[4],
    };

    return (
      <div styleName="container" data-cy="availability-content-sidebar">
        <div styleName="wrapper">
          <Grid padded styleName="content" id="content">
            <Grid.Row>
              {dayId && (
              <VisitRequestContainer
                id={dayId}
                webInstructions={this.state.webInstructions}
                appointmentId={appointmentId}
                open={showScheduleModal}
                onClose={this.closeScheduleModal}
              />
              )}
              <Grid.Column width={12} as="h2" styleName="sidebarHeader">
                <div styleName="schoolName">{schoolName}</div>
                <div styleName="sidebarTitle">
                  <FormattedMessage {...messages.title} />
                </div>
              </Grid.Column>
              <Grid.Column width={4} textAlign="right" aria-hidden="true">
                <Button
                  id="closeExport"
                  icon="close"
                  onClick={closeFunction}
                  aria-label="Close"
                />
              </Grid.Column>
            </Grid.Row>
            {!hideFairs && !hideVisits && (
            <Grid.Row>
              <Grid.Column>
                <Button.Group fluid>
                  <Button styleName={visitsStyle} data-cy="repvisits-visits-button" onClick={this.setVisits}>
                    <FormattedMessage id="hs-availability.visits" defaultMessage="Visits" />
                  </Button>
                  <Button styleName={fairsStyle} data-cy="repvisits-fairs-button" onClick={this.setFairs}>
                    <FormattedMessage id="hs-availability.fairs" defaultMessage="Fairs" />
                  </Button>
                </Button.Group>
              </Grid.Column>
            </Grid.Row>
            )}
            {availability === AVAILABILITY_VISITS && !hideVisits && (
            <Grid.Row>
              {!hasRepVisits && (
              <Grid.Column>
                <FormattedMessage {...messages.noRepVisits} />
              </Grid.Column>
              )}
              {hasRepVisits && (
              <Grid.Column>
                <Grid>
                  <Grid.Row columns="equal">
                    <Grid.Column styleName="week-showing">
                      <FormattedMessage {...messages.showingWeek} />
                      <RepVisitsWeekOf dateFrom={dateFrom} dateTo={dateTo} />
                    </Grid.Column>
                    <Grid.Column textAlign="right">
                      <Popup
                        trigger={(
                          <Button
                            basic
                            size="small"
                            labelPosition="right"
                            icon={<Icon name="calendar outline" />}
                            content={intl.formatMessage(messages.selectWeek)}
                          />
                        )}
                        on="click"
                        open={showDatePicker}
                        onClose={this.toggleDatePicker}
                        onOpen={this.toggleDatePicker}
                      >
                        <DatePicker
                          selectedDays={{ from: dateFrom, to: dateTo }}
                          initialMonth={dateFrom}
                          onDayClick={this.handleDatePickerChanged}
                          enableOutsideDays
                        />
                      </Popup>
                      <Button.Group size="small" as="span">
                        <Button
                          basic
                          icon="triangle left"
                          color="teal"
                          onClick={this.viewPrevWeek}
                          aria-label={intl.formatMessage(messages.prevWeek)}
                        />
                        <Button
                          basic
                          icon="triangle right"
                          color="teal"
                          onClick={this.viewNextWeek}
                          aria-label={intl.formatMessage(messages.nextWeek)}
                        />
                      </Button.Group>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
                {highSchoolBySchoolId && (
                <VisitsAvailabilitySingle
                  fromDate={week[0]}
                  toDate={week[4]}
                  week={week}
                  onAppointmentClick={this.openScheduleModal}
                  schoolId={schoolId}
                />
                )}
              </Grid.Column>
              )}
            </Grid.Row>
            )}
            {availability === AVAILABILITY_FAIRS && !hideFairs && (
            <Grid.Row>
              <Grid.Column>
                <FairsAvailabilityContainer params={params} id={schoolId} />
              </Grid.Column>
            </Grid.Row>
            )}
          </Grid>
        </div>
        {highSchoolBySchoolId && !inTravelPlan &&
          <AddToTravelPlanFooter addToTravelPlanHandler={this.addTravelPlan(highSchoolBySchoolId.id)} />}
        {notification}
      </div>
    );
  }
}

export default injectIntl(Availability);
